/* LookupTour — lightweight Joyride-style guided tour overlay.

Renders a fixed full-screen overlay with a "cutout" highlight around the
current step's target element (via an SVG mask), and a positioned popover
beside it. Pure React + Tailwind, no external dep.

Usage:
  <LookupTour
    steps={[
      { selector: '[data-testid="lookup-tabs"]', title: 'Two ways to look up records',
        body: 'Federal vs. State — pick the tab that matches your judgment.' },
      ...
    ]}
    open={open} onClose={...} onComplete={...}
  />
*/
import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { X, ArrowLeft, ArrowRight } from "@phosphor-icons/react";

const PAD = 8;          // cutout padding around target
const POPOVER_W = 340;
const GAP = 14;         // gap between target and popover

export default function LookupTour({ steps, open, onClose, onComplete, startIndex = 0 }) {
    const [i, setI] = useState(startIndex);
    const [layout, setLayout] = useState({ rect: null, vw: 0, vh: 0 });

    const step = steps[i];
    const { rect, vw, vh } = layout;

    useEffect(() => {
        if (open) setI(startIndex);
    }, [open, startIndex]);

    useLayoutEffect(() => {
        if (!open || !step) return;
        // Allow step-specific side effects (e.g. switch a tab) before measuring
        try { step.beforeShow?.(); } catch { /* noop */ }
        const measure = () => {
            const el = document.querySelector(step.selector);
            if (!el) {
                setLayout({ rect: null, vw: window.innerWidth, vh: window.innerHeight });
                return;
            }
            try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
            requestAnimationFrame(() => {
                const r = el.getBoundingClientRect();
                setLayout({
                    rect: { x: r.left, y: r.top, w: r.width, h: r.height },
                    vw: window.innerWidth,
                    vh: window.innerHeight,
                });
            });
        };
        // Defer initial measure to the next frame so setState happens in an async callback
        // (satisfies React 19's set-state-in-effect rule and lets layout settle).
        const rafId = requestAnimationFrame(measure);
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [open, step]);

    if (!open || !step) return null;

    const isFirst = i === 0;
    const isLast = i === steps.length - 1;

    const next = () => {
        if (isLast) {
            onComplete?.();
            onClose?.();
        } else {
            setI(i + 1);
        }
    };
    const prev = () => { if (!isFirst) setI(i - 1); };

    // Compute popover position: prefer right of the cutout, fall back to below/above
    let popLeft = 24, popTop = 24;
    if (rect) {
        const rightSpace = vw - (rect.x + rect.w + GAP);
        const belowSpace = vh - (rect.y + rect.h + GAP);

        if (rightSpace >= POPOVER_W + 24) {
            popLeft = rect.x + rect.w + GAP;
            popTop = Math.min(Math.max(rect.y, 16), vh - 220);
        } else if (belowSpace >= 220) {
            popLeft = Math.min(Math.max(rect.x, 16), vw - POPOVER_W - 16);
            popTop = rect.y + rect.h + GAP;
        } else {
            // place above
            popLeft = Math.min(Math.max(rect.x, 16), vw - POPOVER_W - 16);
            popTop = Math.max(rect.y - 220, 16);
        }
    }

    return createPortal(
        <div className="fixed inset-0 z-[100]" data-testid="lookup-tour-overlay" role="dialog" aria-label="Tour">
            {/* Dimmed backdrop with SVG mask cutout */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <defs>
                    <mask id="tour-mask">
                        <rect width="100%" height="100%" fill="white" />
                        {rect && (
                            <rect
                                x={rect.x - PAD}
                                y={rect.y - PAD}
                                width={rect.w + PAD * 2}
                                height={rect.h + PAD * 2}
                                rx="8"
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(15, 30, 61, 0.62)" mask="url(#tour-mask)" />
                {rect && (
                    <rect
                        x={rect.x - PAD}
                        y={rect.y - PAD}
                        width={rect.w + PAD * 2}
                        height={rect.h + PAD * 2}
                        rx="8"
                        fill="none"
                        stroke="#FACC15"
                        strokeWidth="2"
                        strokeDasharray="6 4"
                        className="animate-pulse"
                    />
                )}
            </svg>

            {/* Catch outside clicks (but not on the popover) */}
            <button
                type="button"
                aria-label="Close tour"
                className="absolute inset-0 w-full h-full cursor-default"
                onClick={onClose}
                data-testid="lookup-tour-backdrop"
            />

            {/* Popover */}
            <div
                className="absolute bg-white rounded-md shadow-2xl border border-slate-200 p-5"
                style={{ left: popLeft, top: popTop, width: POPOVER_W }}
                data-testid="lookup-tour-popover"
                onClick={(e) => e.stopPropagation()}
                role="document"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                    aria-label="Close tour"
                    data-testid="lookup-tour-close"
                >
                    <X size={14} />
                </button>
                <p className="overline">Step {i + 1} of {steps.length}</p>
                <h3 className="font-serif text-lg mt-2 leading-tight text-[#0F1E3D] pr-6" data-testid="lookup-tour-title">
                    {step.title}
                </h3>
                <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">
                    {step.body}
                </p>

                <div className="mt-5 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs text-slate-500 hover:text-slate-800"
                        data-testid="lookup-tour-skip"
                    >
                        Skip tour
                    </button>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={prev}
                            disabled={isFirst}
                            data-testid="lookup-tour-prev"
                        >
                            <ArrowLeft size={13} className="mr-1" /> Back
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={next}
                            className="bg-[#0F1E3D] hover:bg-[#1A2C57]"
                            data-testid="lookup-tour-next"
                        >
                            {isLast ? "Done" : <>Next <ArrowRight size={13} className="ml-1" /></>}
                        </Button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
